/**
 * The arithmetic and the rules behind "Add to downline".
 *
 * Every cap on this form is measured against something the parent actually
 * holds — their balance, their share, their commission, their bet ceiling — so
 * the rules are worth pinning in tests rather than leaving inline in a 900-line
 * component. The wizard that this replaces had all of it inline; nothing here
 * is new logic, it is the same logic made testable.
 *
 * Nothing in this file formats for the rail or the review card except the error
 * strings, which quote figures through `src/lib/format` like every other money
 * string in the console.
 */

import { displayMoney, pct } from '@/lib/format'
import { parseCurrencyType, type CurrencyType } from '@/lib/currency'
import { descendantRoles, type Account, type GameSwitches, type Role } from '@/data/types'
import {
  confirmPasswordError,
  confirmPasswordErrorOptional,
  passwordError,
  passwordErrorOptional,
} from './passwordPolicy'

/**
 * Eight to twenty characters, lowercase, starting with a letter.
 * Min length 8 matches Winner7 Create Member; `@` is allowed for Lab-style
 * logins (`point@20`, `demo@fancy`) — no email domain required.
 */
export const USERNAME_RE = /^[a-z][a-z0-9._@]{7,19}$/

export const USERNAME_FORMAT_HINT =
  'Eight to twenty characters: lowercase letters, digits, dot, underscore or @, starting with a letter.'

/**
 * The worked example on the rail. A share of "12%" is abstract; "₹12,000 of a
 * ₹1,00,000 loss" is not, and an operator setting terms is thinking in rupees.
 * One lakh because it is the round number this business talks in.
 */
export const ILLUSTRATION_BASE = 100_000

export type Availability = 'idle' | 'checking' | 'free' | 'taken'

/** Every input on the form, held as typed. Numbers stay strings until parsed. */
export interface CreateValues {
  role: Role | null
  username: string
  displayName: string
  /** Collected for future sign-in provisioning (#149); not sent to hierarchy yet. */
  password: string
  confirmPassword: string
  comments: string
  creditLimit: string
  /**
   * Max credit the new agent may hand to each level beneath them.
   * Keys are roles below the chosen level (including Member). Empty when
   * creating a Member — they have no downline.
   */
  downlineMax: Partial<Record<Role, string>>
  /** Sportsbook partnership share (percentage points 0–100). */
  share: string
  /** Casino partnership share (percentage points 0–100). */
  shareCasino: string
  fancy: string
  bookmaker: string
  matchOdds: string
  minBet: string
  maxBet: string
  games: GameSwitches
  /** INR or POINTS (1 point = ₹100). CM sets on TM / direct member; downline inherits. */
  currencyType: 'INR' | 'POINTS'
  /** Lab casinoCurrencyType — Evolution HKD|AED. */
  casinoCurrencyType: 'HKD' | 'AED'
  /** Lab fundistCurrencyType — Crash Games. */
  fundistCurrencyType: 'HKD' | 'AED' | 'USD' | 'SGD' | 'INR'
}

/**
 * Levels beneath `role` that need a max-credit ceiling when creating an agent
 * (Winner7 Super/Master/Player Max Credit Limit cascade).
 */
export function downlineMaxRoles(role: Role): Role[] {
  return descendantRoles(role)
}

export function downlineMaxFieldKey(role: Role): string {
  return `downlineMax.${role}`
}

/**
 * Facts about the creator that cap this form.
 *
 * Backend-UI (Winner7 / Lab User checkCreditLimitIncrease): create max is
 * `creditLimit − Σ children's creditLimits + min(0, balanceUp)`. Wins do not
 * raise assignable credit. Hierarchy exposes that as `remainingToAssign`.
 * We keep ledger `balance` separate and expose `availableCredit` explicitly.
 */
export type ParentTerms = Pick<
  Account,
  'balance' | 'share' | 'commission' | 'betLimits' | 'creditLimit' | 'currencyType'
> & {
  /** Remaining credit line the parent may still assign on create (Lab freeCredit). */
  availableCredit: number
  /**
   * Max credit this parent may hand to each level beneath them (Winner7 cascade).
   * When set, caps the child's own credit limit and the child's downline-max fields.
   */
  downlineMaxCredit?: Partial<Record<Role, number>>
}

/** Remaining line after summing direct children's credit limits (Lab distributed). */
export function remainingCredit(creditLimit: number, allocated: number): number {
  if (!Number.isFinite(creditLimit) || creditLimit < 0) return 0
  if (!Number.isFinite(allocated) || allocated < 0) return Math.max(0, creditLimit)
  return Math.max(0, creditLimit - allocated)
}

/**
 * Lab freeCredit for create/increase:
 * parentCL − distributed + Math.min(0, balanceUp).
 */
export function labAssignableCredit(
  creditLimit: number,
  allocated: number,
  balanceUp: number,
): number {
  const base = remainingCredit(creditLimit, allocated)
  const haircut = Number.isFinite(balanceUp) && balanceUp < 0 ? balanceUp : 0
  return Math.max(0, base + haircut)
}

/* ------------------------------------------------------------------ parse -- */

/**
 * A blank box is not a zero — anything unparseable comes back null so the
 * caller can say "enter a number" rather than silently creating a ₹0 limit.
 */
export function parseNumber(text: string): number | null {
  const cleaned = text.replace(/[₹,%\s]/g, '')
  if (cleaned === '') return null
  const value = Number(cleaned)
  return Number.isFinite(value) ? value : null
}

/* ------------------------------------------------------------- validation -- */

/** Format only — the half of the username rule that does not need the network. */
export function usernameFormatError(username: string): string | null {
  const trimmed = username.trim()
  if (trimmed === '') return 'Pick a username for the new account'
  return USERNAME_RE.test(trimmed) ? null : USERNAME_FORMAT_HINT
}

export function displayNameError(displayName: string): string | null {
  return displayName.trim() === '' ? 'Enter a display name' : null
}

export {
  passwordError,
  passwordErrorOptional,
  confirmPasswordError,
  confirmPasswordErrorOptional,
}

/**
 * Format, then the availability verdict.
 *
 * `checking` and `idle` are deliberately NOT errors. An in-flight probe blocks
 * the submit for a beat (the button says so) and a probe that failed outright
 * must not wedge the form — the server enforces uniqueness either way.
 */
export function usernameError(username: string, availability: Availability): string | null {
  const format = usernameFormatError(username)
  if (format) return format
  return availability === 'taken' ? `${username.trim()} is already taken` : null
}

export function creditError(
  value: number | null,
  availableCredit: number,
  currency: string = 'INR',
): string | null {
  if (value === null) return 'Enter a credit limit, or 0 to start them with none'
  if (value < 0) return 'A credit limit cannot be negative'
  if (value > availableCredit) {
    return `That is more than the ${displayMoney(availableCredit, currency)} credit you still have to assign`
  }
  return null
}

/**
 * Backend-UI editMember: creditLimit must stay above Balance Up (`$rootScope.take`).
 * Here `settlement > 0` means the parent is owed that much (a Take).
 */
export function editCreditError(
  value: number | null,
  availableCredit: number,
  balanceUpFloor: number,
  currency: string = 'INR',
): string | null {
  const base = creditError(value, availableCredit, currency)
  if (base) return base
  if (value !== null && balanceUpFloor > 0 && value < balanceUpFloor) {
    return `Credit limit must be at least ${displayMoney(balanceUpFloor, currency)} (Balance Up)`
  }
  return null
}

/**
 * Cascading max-credit for a level beneath the new agent.
 * Ceiling is the tighter of the new account's own credit limit, the max set for
 * the level immediately above, and the parent's stored max for this level
 * (Winner7 Create Member cascade).
 */
export function downlineMaxError(
  value: number | null,
  ceiling: number | null,
  label: string,
  currency: string = 'INR',
): string | null {
  if (value === null) return `Enter a max credit limit for ${label}`
  if (value < 1) return `Max credit for ${label} must be at least 1`
  if (ceiling === null) return 'Set this account’s credit limit first'
  if (value > ceiling) {
    return `Cannot exceed ${displayMoney(ceiling, currency)} — the tighter of this account’s credit limit, the level above, and your upline’s max for this level`
  }
  return null
}

/**
 * Remaining line the parent may still assign to this child role, after applying
 * the parent's stored downline-max for that role (when present).
 */
export function assignableCreditForRole(
  availableCredit: number,
  childRole: Role,
  parentDownlineMax?: Partial<Record<Role, number>>,
): number {
  const available = Math.max(0, availableCredit)
  const cap = parentDownlineMax?.[childRole]
  if (cap == null || !Number.isFinite(cap)) return available
  return Math.max(0, Math.min(available, cap))
}

/**
 * EditAccountTab `availableForThis` soft ceiling:
 * leftover (parent available already in child currency) + child's current limit,
 * then `assignableCreditForRole` vs parent `downlineMaxCredit` for the child role.
 * Missing leftover (no parent loaded) → current child limit only.
 */
export function editAvailableCreditForThis(opts: {
  leftoverInChildCurrency: number | null | undefined
  childCreditLimit: number
  childRole: Role
  parentDownlineMax?: Partial<Record<Role, number>>
}): number {
  if (opts.leftoverInChildCurrency == null) return opts.childCreditLimit
  const remainingPlusCurrent = Math.max(
    0,
    opts.leftoverInChildCurrency + opts.childCreditLimit,
  )
  return assignableCreditForRole(
    remainingPlusCurrent,
    opts.childRole,
    opts.parentDownlineMax,
  )
}

/** Ceiling for one downline-max field given typed values higher in the cascade. */
export function downlineMaxCeiling(
  role: Role,
  roles: Role[],
  creditLimit: number | null,
  downlineMax: Partial<Record<Role, string>>,
  parentDownlineMax?: Partial<Record<Role, number>>,
): number | null {
  if (creditLimit === null) return null
  let ceiling = creditLimit
  const parentCap = parentDownlineMax?.[role]
  if (parentCap != null && Number.isFinite(parentCap)) {
    ceiling = Math.min(ceiling, parentCap)
  }
  for (const level of roles) {
    if (level === role) return ceiling
    const parsed = parseNumber(downlineMax[level] ?? '')
    if (parsed === null) return null
    ceiling = Math.min(ceiling, parsed)
  }
  return ceiling
}

/**
 * Lab sevenone-1:
 * - **INR**: Member Rate absolute (0–1); My Share display = child − parent; child ≥ parent
 * - **POINTS**: My share = own keep % (any integer ≥ 0); no fall-down floor; no ≤100 cap
 */
/** Lab create-member INR: My Share label = entered Member Rate − parent min.
 *  POINTS: textbox is the keep itself. */
export function myShareKeep(
  entered: number,
  parentRate: number,
  currency: CurrencyType | string = 'POINTS',
): number {
  if (!Number.isFinite(entered)) return 0
  if (parseCurrencyType(currency) === 'INR') {
    if (!Number.isFinite(parentRate)) return 0
    return Math.max(0, entered - parentRate)
  }
  return Math.max(0, entered)
}

export function shareError(
  value: number | null,
  yourShare: number,
  label: 'sportsbook' | 'casino' = 'sportsbook',
  currency: CurrencyType | string = 'POINTS',
): string | null {
  const ccy = parseCurrencyType(currency)
  if (label === 'casino') return casinoShareError(value, ccy)
  const kind = ccy === 'INR' ? 'Member Rate' : 'My share'
  const max = maxShareOf(yourShare, ccy)
  const min = minShareOf(yourShare, ccy)
  if (value === null) return `Enter a ${kind}`
  if (value < 0) return 'A share cannot be negative'
  if (ccy === 'INR') {
    if (min <= 1e-9 && value <= 1e-9) {
      return 'please enter my share greater than 0'
    }
    if (value + 1e-9 < min) {
      return `${kind} must be at least ${shareFmt(min)} (upline rate — Lab fall-down)`
    }
    if (max != null && value > max + 1e-9) {
      return `${kind} must be at most ${shareFmt(max)}`
    }
    return null
  }
  // POINTS: any integer ≥ 0 (CM=0; player stores Master's My Share).
  if (!Number.isInteger(value)) return `${kind} must be a whole number`
  return null
}

/**
 * Live Casino Member Rate validation (Lab create/edit):
 * - POINTS / POINT@1: any finite numeric value
 * - INR: 0–100 inclusive
 */
export function casinoShareError(
  value: number | null,
  currency: CurrencyType | string = 'POINTS',
): string | null {
  const ccy = parseCurrencyType(currency)
  const kind = ccy === 'INR' ? 'casino Member Rate' : 'casino My share'
  if (value === null || !Number.isFinite(value)) return `Enter a ${kind}`
  if (ccy === 'INR') {
    if (value < 0 || value > 100) return `${kind} must be between 0 and 100`
  }
  return null
}

function shareFmt(n: number): string {
  const fixed = n.toFixed(2)
  return fixed.includes('.') ? fixed.replace(/\.?0+$/, '') : fixed
}

/** Ceiling: INR SportsBook 1; POINTS uncapped (null). */
export function maxShareOf(
  _yourShare: number,
  currency: CurrencyType | string = 'POINTS',
): number | null {
  return parseCurrencyType(currency) === 'INR' ? 1 : null
}

/** Live Casino: INR capped at 100; POINTS / POINT@1 have no UI ceiling. */
export function maxCasinoShareOf(currency: CurrencyType | string = 'POINTS'): number | null {
  return parseCurrencyType(currency) === 'INR' ? 100 : null
}

/** INR fall-down floor = parent absolute. POINTS own-keep floor = 0. */
export function minShareOf(
  yourShare: number,
  currency: CurrencyType | string = 'POINTS',
): number {
  if (parseCurrencyType(currency) !== 'INR') return 0
  const max = maxShareOf(yourShare, currency) ?? 1
  const p = Number(yourShare)
  if (!Number.isFinite(p) || p < 0) return 0
  return Math.min(max, p)
}

/** Live Casino has no parent floor (Lab skips INR Live Casino ≥ parent/100). */
export function minCasinoShareOf(_currency: CurrencyType | string = 'POINTS'): number {
  return 0
}

export function shareStep(currency: CurrencyType | string): number {
  return parseCurrencyType(currency) === 'INR' ? 0.01 : 1
}

export function casinoShareStep(currency: CurrencyType | string): number {
  return parseCurrencyType(currency) === 'INR' ? 1 : 0.01
}

export function commissionError(value: number | null, max: number, label: string): string | null {
  if (value === null) return `Enter a ${label} commission, or 0`
  if (value < 0) return 'Commission cannot be negative'
  if (value > max) {
    return `Your own ${label} commission is ${pct(max, 2)} — you cannot pay down more than you receive`
  }
  return null
}

/**
 * An agent carries no bet limits of its own (they are a member-level setting),
 * so a zero on the parent means "no ceiling to inherit", not "nothing allowed".
 */
export function parentMaxBet(parent: Pick<Account, 'betLimits'>): number | null {
  return parent.betLimits.max > 0 ? parent.betLimits.max : null
}

export interface BetLimitErrors {
  min: string | null
  max: string | null
}

/** Member-only. The caller decides whether to ask at all; this decides whether the pair coheres. */
export function betLimitErrors(
  min: number | null,
  max: number | null,
  parentMax: number | null,
): BetLimitErrors {
  const minError =
    min === null
      ? 'Enter a minimum bet'
      : min <= 0
        ? 'The minimum bet must be more than zero'
        : null

  const maxError =
    max === null
      ? 'Enter a maximum bet'
      : min !== null && max <= min
        ? 'The maximum bet must be above the minimum'
        : parentMax !== null && max > parentMax
          ? `Your own maximum is ${displayMoney(parentMax)}`
          : null

  return { min: minError, max: maxError }
}

/* ---------------------------------------------------------------- summary -- */

export interface CreateSummary {
  /** Parsed credit limit, or null while the box is blank. A blank is not a zero. */
  creditLimit: number | null
  /** Credit clamped into remaining line — for the bar only. */
  allocating: number
  remaining: number
  overAllocated: boolean
  /** Parent's remaining credit line before this create. */
  availableCredit: number
  yourShare: number
  /** Their sportsbook share (clamped for INR; raw keep for POINTS). */
  theirShare: number
  /** INR: fall-down Δ (theirShare − yourShare). POINTS: child's own keep. */
  yourGap: number
  /** Absolute share still above them toward leaf max (0 when POINTS uncapped). */
  restOfChain: number
  /** INR: 1. POINTS: null (any integer % — no UI ceiling). */
  maxShare: number | null
  yourCasinoShare: number
  theirCasinoShare: number
  yourCasinoGap: number
  restOfCasinoChain: number
  /** INR: 100. POINTS: null (any numeric — no UI ceiling). */
  maxCasinoShare: number | null
  illustration: { base: number; toYou: number; toThem: number }
}

/**
 * The live consequence of what has been typed, in one object.
 *
 * Everything is clamped for display: an out-of-range figure is reported by the
 * field error, never by a bar that silently rescales itself around an
 * impossible number. That is why the illustration uses the clamped share — a
 * worked rupee figure derived from a rejected input would be a lie with a
 * decimal point on it.
 *
 * Credit bar uses remaining credit line (Backend-UI), not ledger cash.
 */
export function deriveSummary(
  input: Pick<CreateValues, 'creditLimit' | 'share' | 'shareCasino'>,
  parent: ParentTerms,
): CreateSummary {
  const creditLimit = parseNumber(input.creditLimit)
  const shareValue = parseNumber(input.share)
  const shareCasinoValue = parseNumber(input.shareCasino)
  const available = Math.max(0, parent.availableCredit)

  const allocating = creditLimit === null ? 0 : Math.max(0, Math.min(creditLimit, available))
  const remaining = Math.max(0, available - allocating)
  const overAllocated = creditLimit !== null && creditLimit > available

  const yourShare = parent.share.sportsbook
  const ccy = parseCurrencyType(parent.currencyType ?? 'INR')
  const maxShare = maxShareOf(yourShare, ccy)
  const minShare = minShareOf(yourShare, ccy)
  const rawShare = shareValue ?? minShare
  const theirShare =
    maxShare == null
      ? Math.max(rawShare, minShare)
      : Math.min(Math.max(rawShare, minShare), maxShare)
  // INR: fall-down Δ (child − parent). POINTS: textbox is own keep.
  const yourGap = myShareKeep(theirShare, yourShare, ccy)
  const restOfChain = maxShare == null ? 0 : Math.max(0, maxShare - theirShare)

  const yourCasinoShare = parent.share.casino
  const maxCasinoShare = maxCasinoShareOf(ccy)
  const minCasinoShare = minCasinoShareOf(ccy)
  const rawCasino = shareCasinoValue ?? minCasinoShare
  const theirCasinoShare =
    maxCasinoShare == null
      ? rawCasino
      : Math.min(Math.max(rawCasino, minCasinoShare), maxCasinoShare)
  const yourCasinoGap = myShareKeep(theirCasinoShare, yourCasinoShare, ccy)
  const restOfCasinoChain =
    maxCasinoShare == null ? 0 : Math.max(0, maxCasinoShare - theirCasinoShare)

  // INR rates are 0–1; POINTS My share is % of pot (may exceed 100).
  const illDiv = ccy === 'INR' ? 1 : Math.max(100, theirShare, 1)

  return {
    creditLimit,
    allocating,
    remaining,
    overAllocated,
    availableCredit: available,
    yourShare,
    theirShare,
    yourGap,
    restOfChain,
    maxShare,
    yourCasinoShare,
    theirCasinoShare,
    yourCasinoGap,
    restOfCasinoChain,
    maxCasinoShare,
    illustration: {
      base: ILLUSTRATION_BASE,
      toYou: (ILLUSTRATION_BASE * yourGap) / illDiv,
      toThem: (ILLUSTRATION_BASE * theirShare) / illDiv,
    },
  }
}

/** `4 of 5 enabled` for the rail, and the names behind it. */
export function enabledGames(games: GameSwitches, labels: Record<keyof GameSwitches, string>) {
  const keys = Object.keys(labels) as (keyof GameSwitches)[]
  const on = keys.filter((key) => games[key])
  return { on: on.length, total: keys.length, names: on.map((key) => labels[key]) }
}
